import type { Template } from '@/types/template';
import { DeviceType, PaymentMethodEnum } from '@/types/common';
import { getDeviceType } from '@/utils/device';

/**
 * Which express wallet a device's native payment sheet supports.
 * `WEB` has no native express wallet.
 *
 * Returns the canonical `PaymentMethodEnum`; pair with
 * `isPaymentMethodAvailable` (`@/utils/paymentMethodResolvers`) when
 * you need to check it against the BE-returned methods list — keeping
 * "presence in list" out of this file lets non-express branches
 * (paypal/link/card/…) reuse the same helper.
 */
export const expressMethodForDevice = (device: DeviceType): PaymentMethodEnum | undefined => {
  if (device === DeviceType.IOS) return PaymentMethodEnum.APPLE_PAY;
  if (device === DeviceType.ANDROID) return PaymentMethodEnum.GOOGLE_PAY;
  return undefined;
};

/**
 * On phones, templates with a multi-wallet express row should surface ONLY
 * the device-native express wallet (Apple Pay on iOS, Google Pay on Android)
 * — never a second wallet (e.g. Google Pay on iPhone) nor PayPal in the
 * express row. Desktop/tablet (`DeviceType.WEB`) keeps every configured
 * express wallet.
 *
 * The template half of the decision is the `restrictExpressToNativeWallet`
 * feature flag (true for DEFAULT + EMBEDDED_VIEW) rather than a `type === DEFAULT`
 * comparison, so a new template opts in through config instead of needing an
 * edit here — see the leaves-don't-know-template rule on `CheckoutTemplate`.
 */
export const shouldRestrictExpressToNativeWallet = (template: Template): boolean =>
  template.features.restrictExpressToNativeWallet && getDeviceType() !== DeviceType.WEB;
